/**
 * Stable TanStack Query cache scope (ARCHITECTURE.md R24).
 *
 * Query keys must NOT flip when /users/me replaces a placeholder profile with the
 * same JWT session — otherwise every hook refetches at once (budget baselines, manifest,
 * projects) and can exhaust the tenant DB pool; /status then fails and the footer shows
 * "Disconnected".
 */

import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

function usernameFromAccessToken(tok) {
  if (!tok || typeof tok !== 'string') return null;
  try {
    const mid = tok.split('.')[1];
    if (!mid) return null;
    const normalized = mid.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized));
    return typeof payload?.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

function readTenantId() {
  try {
    return localStorage.getItem('tenant_id') || '';
  } catch {
    return '';
  }
}

/**
 * Session-stable scope for React Query keys: tenant + username (JWT sub preferred).
 * Intentionally avoids `user.id` so placeholder → full profile does not bust the cache.
 */
export function useQueryScope() {
  const { user, token } = useAuth();
  return useMemo(() => {
    const tenant = readTenantId();
    const identity =
      user?.username ??
      usernameFromAccessToken(token) ??
      (user?.id != null ? String(user.id) : null) ??
      user?.email ??
      'anon';
    return `${tenant}:${identity}`;
  }, [user?.username, user?.id, user?.email, token]);
}

/** @deprecated Prefer useQueryScope — kept for incremental hook migrations */
export function useLegacyQueryScope() {
  return useQueryScope();
}
